import * as Sentry from "@sentry/react";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const IS_PRODUCTION = import.meta.env.MODE === 'production';

/**
 * Inicializa o Sentry para error tracking e performance monitoring
 */
export function initSentry() {
  if (!SENTRY_DSN) {
    console.warn('⚠️ Sentry DSN não configurado. Error tracking desabilitado.');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    enabled: IS_PRODUCTION, // Só habilita em produção
    
    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Session Replay para debugging visual
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    
    // Performance monitoring sample rate
    tracesSampleRate: IS_PRODUCTION ? 0.1 : 1.0, // 10% em prod, 100% em dev
    
    // Session replay sample rates
    replaysSessionSampleRate: IS_PRODUCTION ? 0.1 : 0, // 10% em prod, 0% em dev
    replaysOnErrorSampleRate: 1.0, // 100% quando há erro
    
    // Configurações de erro
    beforeSend(event, hint) {
      // Filtrar erros de extensões do browser
      const error = hint.originalException as Error;
      if (
        error?.message?.includes('chrome-extension://') ||
        error?.message?.includes('moz-extension://')
      ) {
        return null; // Não enviar
      }
      
      return event;
    },
    
    // Ignorar erros conhecidos/esperados
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'Network request failed',
      'Failed to fetch',
    ],
  });

  console.log('✅ Sentry inicializado com sucesso');
}

/**
 * Captura erro manual com contexto adicional
 */
export function captureError(
  error: Error, 
  context?: Record<string, any>,
  level: Sentry.SeverityLevel = 'error'
) {
  Sentry.captureException(error, {
    level,
    extra: context,
  });
}

/**
 * Adiciona contexto do usuário ao Sentry
 */
export function setUserContext(userId: string, email?: string, name?: string) {
  Sentry.setUser({
    id: userId,
    email,
    username: name,
  });
}

/**
 * Remove contexto do usuário (logout)
 */
export function clearUserContext() {
  Sentry.setUser(null);
}

/**
 * Adiciona breadcrumb (rastro de ações)
 */
export function addBreadcrumb(message: string, category: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

/**
 * Inicia transação de performance (span)
 */
export function startTransaction(name: string, op: string) {
  return Sentry.startSpan({
    name,
    op,
  }, (span) => span);
}
